/**
 * Fase 3 Blokk 3E — Sensor-view for prosjekt-innleveringer
 *
 * Rute: /sensor/project-submissions/:submissionId
 *
 * Layout:
 *   - Venstre: inline-visning av filer (PDF/Word/PPT via iframe/mammoth-pre-rendret)
 *   - Høyre top: KI-forhåndsvurdering (score, flagg, oppsummering) hvis tilgjengelig
 *   - Høyre bunn: Rubric-vurdering (dropdown per kriterium) eller fri score 0-100
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, FileText, Sparkles, Save, CheckCircle, AlertCircle,
  Download, Flag, Lock
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface SubmissionDetail {
  id: string;
  status: string;
  submitted_at: string | null;
  file_paths: string[] | null;
  file_names: string[] | null;
  file_sizes_bytes: number[] | null;
  file_mime_types: string[] | null;
  candidate_code: string | null;
  candidate_full_name: string | null;
  ai_consent_status: string | null;
  component_title: string;
  component_description: string | null;
  component_type: string;
  rubric_id: string | null;
  grading_scale: string;
  pass_threshold: number | null;
  is_anonymous: boolean;
  sensor_score: number | null;
  sensor_grade: string | null;
  sensor_comment: string | null;
  sensor_passed: boolean | null;
  sensor_graded_at: string | null;
  course_title: string | null;
  plan_title: string | null;
}

interface RubricCriterion {
  id: string;
  label: string;
  description: string | null;
  weight: number;
  levels: Array<{ label: string; score: number; description?: string }>;
}

interface Rubric {
  id: string;
  title: string;
  description: string | null;
  is_locked: boolean;
  criteria: RubricCriterion[];
}

interface AiReview {
  id: string;
  completed_at: string | null;
  status: string;
  model: string;
  score: number | null;
  rubric_scores: Array<{ criterion_id: string; score: number; comment: string }> | null;
  flags: string[];
  summary: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  error_message: string | null;
}

interface Payload {
  submission: SubmissionDetail;
  has_extracted_text: boolean;
  extracted_text_length: number;
  rubric: Rubric | null;
  ai_review: AiReview | null;
}

const FLAG_LABELS: Record<string, { label: string; color: string }> = {
  incomplete: { label: 'Ufullstendig', color: 'bg-amber-100 text-amber-900' },
  off_topic: { label: 'Off-topic', color: 'bg-red-100 text-red-900' },
  unclear_understanding: { label: 'Uklar forståelse', color: 'bg-amber-100 text-amber-900' },
  possible_plagiarism: { label: 'Mulig plagiat', color: 'bg-red-100 text-red-900' },
  ai_generated_suspected: { label: 'KI-generert mistenkt', color: 'bg-purple-100 text-purple-900' },
  insufficient_depth: { label: 'For overflatisk', color: 'bg-amber-100 text-amber-900' },
  excellent_work: { label: 'Enestående', color: 'bg-green-100 text-green-900' }
};

export default function SensorProjectView() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const [payload, setPayload] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [sensorScore, setSensorScore] = useState<string>('');
  const [sensorComment, setSensorComment] = useState<string>('');
  const [sensorPassed, setSensorPassed] = useState<boolean | null>(null);
  const [rubricScores, setRubricScores] = useState<Record<string, number>>({});
  const [showFinalize, setShowFinalize] = useState(false);

  const load = useCallback(async () => {
    if (!submissionId) return;
    try {
      setLoading(true);
      const res = await authenticatedFetch(`/api/admin/project-submissions/${submissionId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: Payload = await res.json();
      setPayload(data);
      setSensorScore(data.submission.sensor_score?.toString() || '');
      setSensorComment(data.submission.sensor_comment || '');
      setSensorPassed(data.submission.sensor_passed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load_failed');
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => { load(); }, [load]);

  const save = async (finalize: boolean) => {
    if (!submissionId) return;
    setSaving(true);
    setError(null);
    try {
      const body: any = {
        sensor_score: sensorScore ? Number(sensorScore) : null,
        sensor_comment: sensorComment || null,
        passed: sensorPassed,
        finalize
      };
      if (payload?.rubric && Object.keys(rubricScores).length > 0) {
        body.rubric_scores = Object.entries(rubricScores).map(([criterion_id, score]) => ({
          criterion_id,
          score
        }));
      }
      const res = await authenticatedFetch(`/api/admin/project-submissions/${submissionId}/grade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await load();
      setShowFinalize(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save_failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8">Laster...</div>;
  if (!payload) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-bold">Innlevering ikke funnet</h1>
        {error && <p className="text-red-600 mt-2">{error}</p>}
        <Link to="/sensor/dashboard" className="text-kf-blue-600 underline mt-4 inline-block">
          Tilbake til sensor-dashboard
        </Link>
      </div>
    );
  }

  const { submission, rubric, ai_review, has_extracted_text } = payload;
  const files = (submission.file_names || []).map((name, i) => ({
    name,
    index: i,
    size_bytes: submission.file_sizes_bytes?.[i] || 0,
    mime_type: submission.file_mime_types?.[i] || 'application/octet-stream'
  }));
  const activeFile = files[activeFileIndex];
  const isLocked = submission.status === 'graded';
  const fileUrl = activeFile
    ? `/api/admin/project-submissions/${submissionId}/file/${activeFileIndex}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b px-6 py-4">
        <Link to="/sensor/dashboard" className="inline-flex items-center text-kf-blue-600 hover:underline mb-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Tilbake til sensor-dashboard
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold">{submission.component_title}</h1>
            <p className="text-sm text-gray-600">
              {submission.course_title}
              {submission.plan_title && submission.plan_title !== submission.course_title && (
                <span> · {submission.plan_title}</span>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {submission.is_anonymous
                ? `Anonym kandidat: ${submission.candidate_code}`
                : `${submission.candidate_full_name} (${submission.candidate_code})`}
            </p>
          </div>
          {isLocked && (
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm">
              <Lock className="w-4 h-4 mr-1" /> Ferdig sensurert
            </span>
          )}
        </div>
      </header>

      <div className="grid lg:grid-cols-2 gap-4 p-4">
        {/* VENSTRE: fil-preview */}
        <section className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Innlevering</h2>
            {files.length > 1 && (
              <select
                value={activeFileIndex}
                onChange={e => setActiveFileIndex(Number(e.target.value))}
                className="text-sm border rounded px-2 py-1"
              >
                {files.map(f => <option key={f.index} value={f.index}>{f.name}</option>)}
              </select>
            )}
          </div>
          {activeFile ? (
            <>
              <div className="mb-2 flex items-center text-sm text-gray-600">
                <FileText className="w-4 h-4 mr-1" /> {activeFile.name}
                <span className="ml-2">({(activeFile.size_bytes / 1024 / 1024).toFixed(1)} MB)</span>
                <a
                  href={fileUrl!}
                  download={activeFile.name}
                  className="ml-auto inline-flex items-center text-kf-blue-600 hover:underline text-xs"
                >
                  <Download className="w-3 h-3 mr-1" /> Last ned
                </a>
              </div>
              {activeFile.mime_type.startsWith('application/pdf') ? (
                <iframe
                  src={fileUrl!}
                  title={activeFile.name}
                  className="w-full h-[700px] border rounded"
                />
              ) : (
                <div className="p-8 bg-gray-50 rounded text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-3">
                    Inline-visning er kun støttet for PDF.
                  </p>
                  <a
                    href={fileUrl!}
                    download={activeFile.name}
                    className="inline-flex items-center px-4 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" /> Last ned for å lese
                  </a>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">Ingen filer tilgjengelig.</p>
          )}
          {!has_extracted_text && files.length > 0 && (
            <p className="text-xs text-amber-700 mt-2">
              Tekst er ikke ekstrahert — KI-vurdering vil være begrenset.
            </p>
          )}
        </section>

        {/* HØYRE: KI-panel + rubric-form */}
        <section className="space-y-4">
          {/* KI-panel */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center mb-3">
              <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
              <h2 className="font-semibold">KI-forhåndsvurdering (veiledende)</h2>
            </div>
            {submission.ai_consent_status === 'declined' && (
              <div className="bg-gray-50 p-3 rounded text-sm">
                Kandidaten har valgt bort KI-forhåndsvurdering. Vurder manuelt.
              </div>
            )}
            {submission.ai_consent_status !== 'declined' && !ai_review && (
              <div className="bg-amber-50 p-3 rounded text-sm text-amber-900">
                KI-vurdering ikke tilgjengelig (ikke kjørt enda eller feilet).
              </div>
            )}
            {ai_review && ai_review.status === 'completed' && (
              <>
                {ai_review.score !== null && (
                  <div className="mb-3">
                    <div className="text-sm text-gray-600">Foreslått total-score</div>
                    <div className="text-3xl font-bold text-purple-700">{ai_review.score}/100</div>
                  </div>
                )}
                {ai_review.flags && ai_review.flags.length > 0 && (
                  <div className="mb-3">
                    <div className="text-sm text-gray-600 flex items-center mb-1">
                      <Flag className="w-3 h-3 mr-1" /> Flagg
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {ai_review.flags.map(f => (
                        <span key={f} className={`text-xs px-2 py-1 rounded ${FLAG_LABELS[f]?.color || 'bg-gray-100'}`}>
                          {FLAG_LABELS[f]?.label || f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {ai_review.summary && (
                  <div className="mb-3">
                    <div className="text-sm text-gray-600">Oppsummering</div>
                    <p className="text-sm whitespace-pre-line">{ai_review.summary}</p>
                  </div>
                )}
                <div className="text-xs text-gray-400 border-t pt-2 mt-2">
                  Modell: {ai_review.model} ·
                  Tokens: {(ai_review.prompt_tokens || 0) + (ai_review.completion_tokens || 0)}
                </div>
              </>
            )}
            {ai_review && ai_review.status === 'failed' && (
              <div className="text-red-700 text-sm">KI-vurdering feilet: {ai_review.error_message}</div>
            )}
          </div>

          {/* Rubric eller fri score */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="font-semibold mb-3">Din vurdering</h2>

            {rubric && rubric.criteria.length > 0 ? (
              <div className="space-y-3">
                {rubric.criteria.map(c => (
                  <div key={c.id}>
                    <div className="font-medium text-sm">{c.label} ({c.weight}%)</div>
                    {c.description && <p className="text-xs text-gray-500">{c.description}</p>}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.levels.map((lvl, i) => (
                        <button
                          key={i}
                          type="button"
                          disabled={isLocked}
                          onClick={() => setRubricScores(s => ({ ...s, [c.id]: lvl.score }))}
                          className={`text-xs px-2 py-1 rounded border ${
                            rubricScores[c.id] === lvl.score
                              ? 'bg-kf-blue-600 text-white border-kf-blue-700'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-kf-blue-400'
                          }`}
                        >
                          {lvl.label} ({lvl.score})
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">Total-score (0-100)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={sensorScore}
                  onChange={e => setSensorScore(e.target.value)}
                  disabled={isLocked}
                  className="w-32 border rounded px-3 py-2"
                />
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Bestått?</label>
              <div className="flex gap-2">
                <button
                  disabled={isLocked}
                  onClick={() => setSensorPassed(true)}
                  className={`px-3 py-1 rounded border text-sm ${
                    sensorPassed === true ? 'bg-green-100 border-green-500 text-green-800' : 'border-gray-300'
                  }`}
                >Bestått</button>
                <button
                  disabled={isLocked}
                  onClick={() => setSensorPassed(false)}
                  className={`px-3 py-1 rounded border text-sm ${
                    sensorPassed === false ? 'bg-red-100 border-red-500 text-red-800' : 'border-gray-300'
                  }`}
                >Ikke bestått</button>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Kommentar til kandidat</label>
              <textarea
                value={sensorComment}
                onChange={e => setSensorComment(e.target.value)}
                disabled={isLocked}
                rows={4}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Begrunnelse / tilbakemelding..."
              />
            </div>

            {ai_review && ai_review.score !== null && sensorScore && (
              Math.abs(Number(sensorScore) - ai_review.score) >= 15 && (
                <div className="mt-3 bg-amber-50 p-2 rounded text-xs text-amber-900 flex items-start">
                  <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                  Avvik {Math.abs(Number(sensorScore) - ai_review.score)} poeng fra KI — kommentar anbefales.
                </div>
              )
            )}

            {error && (
              <div className="mt-3 bg-red-50 text-red-800 p-2 rounded text-sm">{error}</div>
            )}

            {!isLocked && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => save(false)}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4 mr-1" /> Lagre utkast
                </button>
                <button
                  onClick={() => setShowFinalize(true)}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Marker som ferdig sensurert
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      {showFinalize && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-bold text-lg mb-2">Finaliser sensur?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Du låser vurderingen for denne kandidaten. Du kan ikke endre den etterpå uten systemadmin-hjelp.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowFinalize(false)} className="px-4 py-2 border rounded">Avbryt</button>
              <button
                onClick={() => save(true)}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {saving ? 'Finaliserer...' : 'Finaliser'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
